import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PAYMENT_INTERVAL, SLUGS_TO_FILTERS } from '../constants/app';
import { APP_ROUTES, getRoute } from '../route';
import { doGetPublicPlansRequest } from '../store/action/challengesAndCourses';
import {
  setSelectedFilters,
  setUniversalLoading,
} from '../store/action/common';
import { doPaymentInitiationRequest } from '../store/action/products';
import { getUserAttribute } from '../utils/helpers';
import { encodeBase64Value, encodeURLParams } from '../utils/url';
import { getItem } from '../utils/cache';
import { extractJSON } from '../services/jsonUtil';

export const useCourseDetail = (
  history,
  match,
  p_interval = ['lowback', 'LowBackFB'].includes(match?.params?.slug)
    ? PAYMENT_INTERVAL.YEARLY
    : PAYMENT_INTERVAL.MONTHLY,
) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const localSlug = extractJSON('localSlug');
  const stableLocalSlug =
    typeof localSlug === 'string' ? localSlug : JSON.stringify(localSlug) ?? '';

  const getSlugFromMatch = value => {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value)?.params?.slug;
      } catch (error) {
        return null;
      }
    }
    return value?.params?.slug;
  };

  const normalizeSlug = value =>
    typeof value === 'string'
      ? value.toLowerCase().trim().replace(/-/g, ' ').replace(/\s+/g, ' ')
      : '';

  const getCurrentSlug = () => {
    const urlParams = new URLSearchParams(history?.location?.search);
    return getSlugFromMatch(match) || urlParams.get('page') || localSlug;
  };

  // const [isCourseLoading, setIsCourseLoading] = useState(false);
  const [courseDetail, setCourseDetail] = useState(() => {
    try {
      const savedCourse = sessionStorage.getItem('courseDetail');
      if (!savedCourse) return null;

      const parsedCourse = JSON.parse(savedCourse);
      const requestedSlug = normalizeSlug(getCurrentSlug());
      const savedSlug = normalizeSlug(parsedCourse?.attributes?.slug);

      // Clear stale session data when route slug and cached slug do not match.
      if (requestedSlug && savedSlug && requestedSlug !== savedSlug) {
        sessionStorage.removeItem('courseDetail');
        return null;
      }

      return parsedCourse;
    } catch (error) {
      console.error('Error reading from sessionStorage', error);
      return null;
    }
  });
  const [challengeError, setChallengeError] = useState(false);
  const { selectedPlan } = useSelector(state => state.product);
  // const { publicPlans } = useSelector(state => state.challengesAndCourses);
  // const redirectToCourse = getItem('redirectToCourse');

  useEffect(() => {
    // This effect syncs the courseDetail state with sessionStorage.
    try {
      if (courseDetail) {
        sessionStorage.setItem('courseDetail', JSON.stringify(courseDetail));
      } else {
        // If courseDetail is null, remove it from storage.
        sessionStorage.removeItem('courseDetail');
      }
    } catch (error) {
      console.error('Error writing to sessionStorage', error);
    }
  }, [courseDetail]);

  useEffect(() => {
    const slug = getCurrentSlug();
    const interval = selectedPlan
      ? selectedPlan?.attributes?.interval
      : p_interval;
    if (slug) {
      dispatch(
        doGetPublicPlansRequest({
          interval,
          callback: plans => {
            if (typeof slug === 'string') {
              const course = plans.find(
                plan =>
                  normalizeSlug(plan?.attributes?.slug) === normalizeSlug(slug),
              );
              setCourseDetail(course || null);
            }
            setIsLoading(false);
          },
        }),
      );
    } else {
      setCourseDetail(null);
      setIsLoading(false);
    }
  }, [
    history?.location?.search,
    stableLocalSlug,
    match?.params?.slug,
    p_interval,
    selectedPlan?.attributes?.interval,
  ]);

  const getRedirectionURL = courseDetail => {
    const { card_color, title, plan_type } = courseDetail?.attributes || {};
    const plan_id = courseDetail?.id;
    const params = {
      card_color,
      plan_type,
      title,
    };
    const options = {
      pathParam: {
        id: plan_id,
      },
      searchParam: {
        params: encodeURLParams(params),
      },
    };
    const url =
      plan_type === 'plan'
        ? APP_ROUTES.HOME_SUB_SECTION
        : APP_ROUTES.PREMIUM_COURSES_DETAILS;
    const route = getRoute(url, options);
    return route;
  };

  const getPurchaseKey = plan_type => {
    switch (plan_type) {
      case 'challenge':
        return 'challenges';
      case 'course':
        return 'courses';
      default:
        return plan_type;
    }
  };

  const redirectUser = courseDetail => {
    const { title, product_id, plan_type } = courseDetail?.attributes || {};
    const plan_id = courseDetail?.id;
    const route = getRedirectionURL(courseDetail);
    const purchases = getUserAttribute('purchases');
    const subscriptionDetails = getUserAttribute('subscription_details');
    const { ends_at, subscribed } = subscriptionDetails || {};
    const activeSubscription =
      subscribed && ends_at && new Date() <= new Date(ends_at);
    const isAlreadyPurchased =
      plan_type === 'plan'
        ? activeSubscription
        : purchases?.[getPurchaseKey(plan_type)]?.includes(
            Number(courseDetail?.id),
          );
    if (isAlreadyPurchased) {
      const onBoardingCompleted = getUserAttribute('onboarding_completed');
      if (onBoardingCompleted) {
        history.push(route);
      } else {
        const url = getRoute(APP_ROUTES.FITNESS, {
          searchParam: {
            page: encodeBase64Value(route),
            special_purchase: true,
          },
        });
        history.push(url);
      }
    } else {
      const urlParams = new URLSearchParams(history?.location?.search);
      const page = urlParams.get('page');
      const slug = match?.params?.slug || page;
      const payload = {
        product_id,
        plan_id,
        page: encodeBase64Value(route),
        name: encodeURI(title),
        errorCallBack: () => setChallengeError(true),
        plan_type: encodeURI(slug),
        isFromCoursePage: true, // indication that the order request came from course page
      };
      if (plan_type === 'plan' && selectedPlan) delete payload.product_id;
      dispatch(doPaymentInitiationRequest(payload));
    }
  };

  // const handleCourseRedirection = () => {
  //   if (courseDetail) {
  //     const slug = match?.params?.slug;
  //     const selectedFilters = SLUGS_TO_FILTERS[slug];
  //     if (selectedFilters) {
  //       dispatch(
  //         setSelectedFilters({
  //           selectedFilters,
  //           callback: () => {
  //             redirectUser(courseDetail);
  //           },
  //         }),
  //       );
  //     } else {
  //       redirectUser(courseDetail);
  //     }
  //   } else {
  //     const urlParams = new URLSearchParams(history.location.search);
  //     const page = urlParams.get('page');
  //     let slug = match?.params?.slug || page;
  //     const p_interval = getItem('selectedPlan');
  //     const interval =
  //       JSON.parse(p_interval)?.attributes?.interval || p_interval;
  //       setIsCourseLoading(true);
  //     dispatch(
  //       doGetPublicPlansRequest({
  //         interval,
  //         callback: plans => {
  //           if (slug === 'Free Trial Page') {
  //             // Added due to issue caused when signup/login from /freetrial page
  //             slug = null;
  //           }
  //           const course = plans.find(plan => plan?.attributes?.slug === slug);
  //           if (course) {
  //             const selectedFilters = SLUGS_TO_FILTERS[slug];
  //             if (selectedFilters) {
  //               dispatch(
  //                 setSelectedFilters({
  //                   selectedFilters,
  //                   callback: () => {
  //                     redirectUser(course);
  //                   },
  //                 }),
  //               );
  //             } else {
  //               redirectUser(course);
  //             }
  //           } else {
  //             history.push(APP_ROUTES.CHALLENGES);
  //           }
  //         },
  //         // setLoading: setIsCourseLoading,
  //       }),
  //     );
  //   }
  // };

  const handleCourseRedirection = async () => {
    try {
      // setIsCourseLoading(true);
      dispatch(setUniversalLoading(true));
      const routeSlug = normalizeSlug(getCurrentSlug());
      let cachedCourseDetail = courseDetail;

      if (!cachedCourseDetail) {
        try {
          cachedCourseDetail = JSON.parse(
            sessionStorage.getItem('courseDetail'),
          );
        } catch (error) {
          cachedCourseDetail = null;
        }
      }

      const isCachedCourseForCurrentSlug =
        cachedCourseDetail &&
        (!routeSlug ||
          normalizeSlug(cachedCourseDetail?.attributes?.slug) === routeSlug);

      if (isCachedCourseForCurrentSlug) {
        const slug = getSlugFromMatch(match);
        const selectedFilters = SLUGS_TO_FILTERS[slug];

        if (selectedFilters) {
          await new Promise(resolve => {
            dispatch(
              setSelectedFilters({
                selectedFilters,
                callback: resolve,
              }),
            );
          });
        }

        redirectUser(cachedCourseDetail);
        dispatch(setUniversalLoading(false));
        return;
      }

      // If courseDetail doesn't exist, fetch public plans
      const urlParams = new URLSearchParams(history.location.search);
      let slug =
        (typeof match === 'string'
          ? JSON.parse(match)?.params?.slug
          : match?.params?.slug) || urlParams.get('page');
      const p_interval = getItem('selectedPlan');
      const interval =
        JSON.parse(p_interval)?.attributes?.interval || p_interval;
      // let plans = [];
      // if(publicPlans.length === 0){
      const plans = await new Promise((resolve, reject) => {
        dispatch(
          doGetPublicPlansRequest({
            interval,
            callback: resolve,
            onError: reject, // Make sure doGetPublicPlansRequest supports this
          }),
        );
      });
      // }
      // else{
      //   plans = publicPlans;
      // }

      if (slug === 'Free Trial Page') {
        slug = null;
      }

      const course = plans?.find(plan => plan?.attributes?.slug === slug);
      if (course) {
        const selectedFilters = SLUGS_TO_FILTERS[slug];
        if (selectedFilters) {
          await new Promise(resolve => {
            dispatch(
              setSelectedFilters({
                selectedFilters,
                callback: resolve,
              }),
            );
          });
        }
        redirectUser(course);
      } else {
        history.push(APP_ROUTES.CHALLENGES);
      }
      // setIsCourseLoading(false);
      dispatch(setUniversalLoading(false));
    } catch (error) {
      // setIsCourseLoading(false);
      dispatch(setUniversalLoading(false));
      console.error('Error during course redirection:', error);
      history.push(APP_ROUTES.CHALLENGES); // fallback in case of error
    }
  };

  return {
    challengeError,
    handleCourseRedirection,
    isLoading,
    setChallengeError,
    courseDetail,
    // isCourseLoading,
  };
};
